import { Injectable, inject } from '@angular/core';
import { ChatConfigService } from './chat-config.service';

export interface ChatSettings {
  sendWithEnter: boolean;
  textSize: 'small' | 'normal' | 'large';
}

@Injectable({
  providedIn: 'root'
})
export class ChatSettingsService {
  private readonly configService = inject(ChatConfigService);

  private readonly STORAGE_KEY = 'gematik_chatbot_settings';
  private readonly defaultSettings: ChatSettings = {
    sendWithEnter: true,
    textSize: 'normal'
  };

  constructor() {
  }

  /**
   * Loads settings from localStorage. If none exist, returns default settings.
   */
  loadSettings(): ChatSettings {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Validate that the parsed object has the expected structure
        return {
          sendWithEnter: typeof parsed.sendWithEnter === 'boolean'
            ? parsed.sendWithEnter
            : this.defaultSettings.sendWithEnter,
          textSize: ['small', 'normal', 'large'].includes(parsed.textSize)
            ? parsed.textSize
            : this.defaultSettings.textSize
        };
      }
    } catch (error) {
      console.error('Error loading chatbot settings from localStorage:', error);
    }

    // If no settings found or error occurred, save and return defaults
    this.saveSettings(this.defaultSettings);
    return { ...this.defaultSettings };
  }

  /**
   * Saves settings to localStorage.
   */
  saveSettings(settings: ChatSettings): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(settings));
    } catch (error) {
      console.error('Error saving chat settings to localStorage:', error);
    }
  }

  /**
   * Resets settings to default values.
   */
  resetSettings(): ChatSettings {
    this.saveSettings(this.defaultSettings);
    return { ...this.defaultSettings };
  }
}
